import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";

const MAIN = ["quotes", "user_machines", "customers", "files", "rfqs", "offers", "notifications", "payment_transactions"];

function DeletionCounts({ counts, t }) {
  const other = Object.entries(counts).filter(([k]) => !MAIN.includes(k)).reduce((s, [, v]) => s + v, 0);
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs" data-testid="delete-user-counts">
      {MAIN.map((k) => (
        <div key={k} className="flex justify-between text-slate-300" data-testid={`delete-count-${k}`}>
          <span>{t(`admin.coll.${k}`)}</span><span className="font-mono-data">{counts[k] || 0}</span>
        </div>
      ))}
      <div className="flex justify-between text-slate-400" data-testid="delete-count-other">
        <span>{t("admin.coll.other")}</span><span className="font-mono-data">{other}</span>
      </div>
    </div>
  );
}

export default function DeleteUserDialog({ open, onOpenChange, target, onDeleted, t }) {
  const [preview, setPreview] = useState(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setTyped(""); setError(""); setPreview(null);
    api.get(`/admin/users/${target.id}/deletion-preview`)
      .then((r) => setPreview(r.data))
      .catch((e) => setError(e.response?.data?.detail || t("common.error")));
  }, [open, target.id, t]);

  const matches = typed.trim().toLowerCase() === target.email.toLowerCase();
  const confirm = async () => {
    setBusy(true); setError("");
    try {
      await api.delete(`/admin/users/${target.id}`, { data: { confirm_email: typed.trim() } });
      toast.success(t("admin.deleteDone", { email: target.email }));
      onDeleted(target.id);
    } catch (e) { setError(e.response?.data?.detail || t("common.error")); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-slate-950 border-slate-800 text-slate-100" data-testid="delete-user-dialog">
        <DialogHeader>
          <DialogTitle>{t("admin.deleteTitle")}</DialogTitle>
          <DialogDescription className="text-slate-400">{t("admin.deleteWillRemove")}</DialogDescription>
        </DialogHeader>
        {!preview && !error && <div className="text-xs text-slate-500">{t("common.loading")}</div>}
        {preview && <DeletionCounts counts={preview.counts} t={t} />}
        {preview?.blocked && (
          <div role="alert" className="text-sm text-amber-200 border border-amber-500/40 bg-amber-500/10 rounded-md px-3 py-2" data-testid="delete-user-blocked">{preview.blocked_message}</div>
        )}
        {preview && !preview.blocked && (
          <label className="block text-xs text-slate-400">
            {t("admin.deleteTypeEmail")} <span className="font-mono-data text-slate-200">{target.email}</span>
            <Input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" className="mt-1 bg-slate-900 border-slate-700 text-slate-100" data-testid="delete-user-confirm-input" />
          </label>
        )}
        {error && <div role="alert" className="text-sm text-red-300" data-testid="delete-user-error">{error}</div>}
        <DialogFooter className="gap-2">
          <button onClick={() => onOpenChange(false)} className="px-3 py-2 rounded-md text-xs border border-slate-600 text-slate-200" data-testid="delete-user-cancel">{t("common.cancel")}</button>
          <button onClick={confirm} disabled={!preview || !!preview.blocked || !matches || busy}
            className="px-3 py-2 rounded-md text-xs border border-red-500 bg-red-600 text-white font-semibold disabled:opacity-40" data-testid="delete-user-confirm">
            {busy ? t("common.loading") : t("admin.deleteConfirm")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
